<template>
  <div
    v-if="product.isSoldOut"
    class="rounded-xl bg-product-sold-out/10 p-4 text-md text-product-sold-out max-md:mx-5"
  >
    {{ $t('global.sold_out_long') }}
  </div>

  <template v-else-if="!hasOneVariantOnly">
    <div class="">
      <div class="flex h-12 items-center space-x-4">
        <SFButton
          :accessibility-label="$t('variant_picker.select_size')"
          class="group inline-flex size-full justify-between gap-0 rounded-xl border border-gray-300 bg-secondary-200 !px-3.5 !py-2.5 font-semibold leading-5 hover:bg-white"
          size="sm"
          variant="raw"
          @click="handleOpenSizeSelector"
        >
          <slot name="default">
            <span
              v-if="
                getFirstAttributeValue(
                  activeVariant?.attributes,
                  isShoeProduct ? 'size' : 'sizeOther',
                )?.label
              "
              class="font-medium text-anthracite-800"
            >
              {{ activeSizeByCountryKey }}
            </span>
            <span v-else class="font-medium text-gray-600">
              {{ $t('variant_picker.select_size') }}
            </span>
          </slot>
          <template #append-icon>
            <IconSnipesArrowsmalldown class="size-4 font-semibold" />
          </template>
        </SFButton>
      </div>
    </div>
  </template>

  <SFAddToBasket
    :add-item-to-basket="() => addItemToBasket(basketItem)"
    :is-sold-out-or-out-of-stock="isSoldOutOrOutOfStock"
    :product="product"
    :variant="activeVariant"
    class="mb-6 mt-4 md:mb-9"
  />

  <SFProductSubscription
    v-if="isProductSubscriptionEligible(product)"
    :product="product"
    :quantity="quantity"
    :variant="activeVariant"
    class="mt-4"
    @add-item-to-basket="addItemToBasket($event)"
  />

  <SFMobileMessageCard
    v-model:show="showMobileMessage"
    :action-text="$t('add_to_basket.notification.view_basket')"
    :title="
      $t('add_to_basket.notification.add_to_basket_success', {
        productName: name,
      })
    "
    @click-action="openStore"
  >
    <SFModalItemCard
      v-if="activeVariant"
      :has-colour="false"
      :product="product"
      :tab="shoesTabs"
      :variant="activeVariant"
    />
  </SFMobileMessageCard>
</template>

<script lang="ts" setup>
import {
  computed,
  defineAsyncComponent,
  hydrateOnVisible,
  ref,
  watch,
} from 'vue'
import {
  getFirstAttributeValue,
  type Product,
  type Promotion,
} from '@scayle/storefront-nuxt'
import { SFButton } from '#storefront-ui/components'
import { isProductSubscriptionEligible } from '#storefront-subscription/helpers/subscription'
import { SFProductSubscription } from '#storefront-subscription/components'
import {
  type AddToBasketItem,
  useBasketActions,
  useFormat,
  useProductBaseInfo,
  useRouteHelpers,
  useTrackingEvents,
} from '~/composables'
import useSizeSelector from '~/composables/useSizeSelector'
import SFAddToBasket from '~/components/product/detail/SFAddToBasket.vue'
import { useDefaultBreakpoints } from '#storefront-ui'
import SFModalItemCard from '~/components/SFModalItemCard.vue'
import { routeList } from '~/utils'
import { useProductClickStorage } from '~/composables/useProductClickStorage'

const SFMobileMessageCard = defineAsyncComponent({
  loader: () => import('~/components/order/SFMobileMessageCard.vue'),
  hydrate: hydrateOnVisible(),
})

const { product } = defineProps<{
  product: Product
  promotion?: Promotion
}>()

const { formatShoeSize } = useFormat()
const { trackAddToBasket } = useTrackingEvents()
const { addItem } = useBasketActions()

const { hasOneVariantOnly, name, isShoeProduct, variants } = useProductBaseInfo(
  () => product,
)
const { greater } = useDefaultBreakpoints()
const {
  setActiveVariant,
  setProduct,
  activeVariant,
  toggleSizeSelector,
  isOpen,
  activeSizeTab,
} = useSizeSelector()
const showMobileMessage = ref(false)
const shouldAddAfterVariantSelected = ref(false)
const quantity = ref(1)
const { localizedNavigateTo } = useRouteHelpers()

watch(
  [hasOneVariantOnly, product, variants],
  ([newHasOneVariantOnly, newProduct, newVariants]) => {
    if (newHasOneVariantOnly) {
      setProduct(newProduct)
      setActiveVariant(newVariants[0])
    }
  },
  {
    immediate: true,
  },
)

watch(
  () => activeVariant.value,
  async (newVariant) => {
    quantity.value = 1
    if (shouldAddAfterVariantSelected.value && newVariant) {
      shouldAddAfterVariantSelected.value = false
      await addItemToBasket(basketItem.value)
    }
  },
)

const shoesTabs = computed(() =>
  activeSizeTab.value !== 'sizeOther' ? activeSizeTab.value : '',
)
const activeSizeKey = computed(() => {
  const sizeMapping = {
    EU: 'sizeEu',
    US: 'sizeUs',
    UK: 'sizeUk',
  }
  return (
    sizeMapping[activeSizeTab.value as keyof typeof sizeMapping] ?? 'sizeOther'
  )
})

const activeSizeByCountryKey = computed(() => {
  return isShoeProduct.value
    ? formatShoeSize(
        getFirstAttributeValue(
          activeVariant.value?.attributes,
          activeSizeKey.value,
        )?.label,
      )
    : getFirstAttributeValue(activeVariant.value?.attributes, 'sizeOther')
        ?.label
})

const openStore = () => {
  localizedNavigateTo(routeList.cart)
}

const isDesktop = computed(() => greater('md').value)
const basketItem = computed<AddToBasketItem | undefined>(() => {
  if (!activeVariant.value) {
    return
  }
  return {
    productName: name.value,
    variantId: activeVariant.value?.id,
    quantity: quantity.value,
  }
})

const isSoldOutOrOutOfStock = computed(
  () =>
    (activeVariant.value && activeVariant.value?.stock.quantity <= 0) ||
    product.isSoldOut,
)

const handleOpenSizeSelector = () => {
  if (product) {
    setProduct(product)
  }
  if (activeVariant.value) {
    setActiveVariant(activeVariant.value)
  }
  toggleSizeSelector()
}

const addItemToBasket = async (item: AddToBasketItem | undefined) => {
  if (!activeVariant.value) {
    if (!isOpen.value) {
      setProduct(product)
      toggleSizeSelector()
    }
    shouldAddAfterVariantSelected.value = true
    return
  }

  if (!item) {
    console.warn('No item found to add to basket') // Should not happen
    return
  }

  try {
    await addItem({
      ...item,
      customData: { activeSizeTab: activeSizeTab.value },
    })

    if (!isDesktop.value) {
      showMobileMessage.value = true

      setTimeout(() => (showMobileMessage.value = false), 3000)
    }

    const { getAllProductClicks } = useProductClickStorage()

    trackAddToBasket({
      product,
      products: getAllProductClicks(),
      variant: activeVariant.value,
      quantity: item.quantity,
      location: 'product detail page',
    })
  } catch {
    /* empty */
  }
}
</script>
